import { useEffect, useMemo, useState } from "react";
import Modal from "@shared/v2/Modal";
import { AccountProduct, ProductFamily } from "./types";
import Button from "@shared/v2/Button";
import Select from "react-select";
import DatePicker from "@shared/v2/DatePicker";
import FieldLabel from "@shared/v2/FieldLabel";
import dayjs from "@shared/dayjs";

interface UpsertProductModalProps {
  accountProduct?: Partial<AccountProduct>;
  onUpsertAccountProduct: (accountProduct: Partial<AccountProduct>) => void;
  onClose: () => void;
  productFamily?: ProductFamily;
}

const UpsertAccountProductModal: React.FC<UpsertProductModalProps> = ({
  accountProduct: propAccountProduct,
  onClose,
  productFamily,
  onUpsertAccountProduct,
}) => {
  const [accountProduct, setAccountProduct] = useState<Partial<AccountProduct>>({});
  const options = useMemo(
    () =>
      productFamily?.products.map((product) => ({
        label: product.name,
        value: product.id,
      })) || [],
    [productFamily],
  );

  useEffect(
    () => setAccountProduct(propAccountProduct || { startedAt: dayjs().format("MM/DD/YYYY") }),
    [propAccountProduct],
  );

  return (
    <Modal
      show={Boolean(productFamily)}
      onHide={onClose}
      dialogClassName="tw-flex tw-items-center tw-justify-center tw-h-full"
      contentClassName="tw-max-w-[640px] tw-max-h-[90vh] tw-w-full tw-flex tw-flex-col tw-gap-[32px]"
    >
      {/* @ts-expect-error props */}
      <Modal.Header title={`${accountProduct?.id ? "Edit" : "Add"} ${productFamily?.typeName}`} closeButton />
      <Modal.Body className="tw-flex tw-flex-col tw-gap-[24px]">
        <Select
          options={options}
          placeholder="Select a product to add"
          required
          value={options.find((o) => o.value === accountProduct.productId) || null}
          onChange={(o) => setAccountProduct({ ...accountProduct, productId: o?.value })}
        />
        <div className="tw-flex tw-flex-col tw-gap-[8px]">
          {/* @ts-expect-error props */}
          <FieldLabel label="Start Date" isRequired />
          <DatePicker
            maxDate={dayjs().format("MM/DD/YYYY")}
            value={accountProduct.startedAt || ""}
            onChange={(date) => {
              if (date) setAccountProduct({ ...accountProduct, startedAt: date as string });
            }}
          />
        </div>
        <div className="tw-flex tw-flex-col tw-gap-[8px]">
          {/* @ts-expect-error props */}
          <FieldLabel label="End Date" />
          <DatePicker
            minDate={accountProduct.startedAt || undefined}
            value={accountProduct.endedAt || ""}
            onChange={(date) => {
              setAccountProduct({
                ...accountProduct,
                endedAt: (date as string) || null,
              });
            }}
          />
        </div>
      </Modal.Body>
      <Modal.Footer className="tw-flex tw-justify-between tw-gap-[16px]">
        {/* @ts-expect-error props */}
        <Button size="medium" schema="tertiary" onClick={onClose}>
          Cancel
        </Button>
        {/* @ts-expect-error props */}
        <Button
          size="medium"
          onClick={() =>
            onUpsertAccountProduct({
              ...accountProduct,
              startedAt: accountProduct.startedAt
                ? dayjs(accountProduct.startedAt).format("YYYY-MM-DD")
                : undefined,
              endedAt: accountProduct.endedAt
                ? dayjs(accountProduct.endedAt).format("YYYY-MM-DD")
                : accountProduct.endedAt,
            })
          }
        >
          {accountProduct?.id ? "Update" : "Add"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default UpsertAccountProductModal;
